import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DateTime } from 'luxon'
import { FiArrowLeft, FiSettings } from 'react-icons/fi'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Alert, LocationMap, MenuAction, PageMenu } from '@/components/ui'
import { PageTitle, useTitleInset } from '@/components/ui/page-header/AppTitleBar'
import { useApi, useAuth } from '@/hooks'
import { AlertMarker } from '@/insights/assets/historyGraph/types'
import { getUserTimezone } from '@/insights/common/timezone'
import { formatUserDate } from '@/utils/formatDate'

import {
  AlertActions,
  AlertStatusesProvider,
  AlertSummaryCard,
  StatusManagement,
  AlertAcknowledgementErrorModal,
  RecordActionModal,
  InitialAlertAcknowledgementSkeleton,
  TemperatureGraphPanel,
  FuelGraphPanel,
} from './components'
import type {
  AlertActionRecord,
  AlertAckView,
  AlertSummaryRecord,
  BlockingModalState,
  SaveRecordActionPayload,
} from './types'

interface ApiActionRow {
  user: string | null
  userId: number | null
  action: string
  note: string | null
  actionCreatedAt: string
}

const toIsoString = (value?: string | Date): string => {
  if (value instanceof Date) {
    return value.toISOString()
  }

  return typeof value === 'string' ? value : ''
}

const getBlockingModalState = (code?: number): BlockingModalState | null => {
  if (code === 404) {
    return {
      title: 'Alert Not Found',
      error: 'We couldn’t find that alert. The link may be outdated or incorrect. Please check the link and try again.',
    }
  }

  if (code === 403) {
    return {
      title: 'Oops! Access Needed',
      error: 'You don’t currently have access to this feature. Please contact Cooltrax support if you need help.',
    }
  }

  return null
}

const AlertAcknowledgementPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const titleInset = useTitleInset()
  const { api } = useApi()
  const { hasPermission } = useAuth()

  // Record Action is available to everyone; only Status Management ("Manage
  // Statuses") is restricted to staff. The server-computed `staff` flag follows
  // the effective (possibly impersonated) user, so when a staff user impersonates
  // a customer the control follows the customer account.
  const isStaff = hasPermission('staff')

  const [view, setView] = useState<AlertAckView>('alert-acknowledgement')
  const [summary, setSummary] = useState<AlertSummaryRecord | null>(null)
  const [pageError, setPageError] = useState('')
  const [blockingError, setBlockingError] = useState<BlockingModalState | null>(null)
  const [isPageLoading, setIsPageLoading] = useState(true)
  const title = view === 'alert-acknowledgement' ? 'Alert Acknowledgement' : 'Status Management'
  const isStatusView = view === 'status-management'
  const [showMap, setShowMap] = useState(true)

  const alertId = searchParams.get('alertId')
  const companyId = Number(searchParams.get('companyId'))
  const readOnly = searchParams.get('readOnly') === 'true'
  const readOnlyParam = readOnly ? '&readOnly=true' : ''

  const [actionRows, setActionRows] = useState<AlertActionRecord[]>([])
  const [actionsError, setActionsError] = useState('')
  const [isActionsLoading, setIsActionsLoading] = useState(false)
  // Monotonic counter used to bust the API's 30s server-side cache when we need
  // the actions list refreshed immediately after a write (acknowledge / record action).
  const actionsCacheBustRef = useRef(0)

  // Default the chart to the day the alert was raised (00:00 → 23:59), per the ticket MEGA-659. Built in
  // the user's PROFILE zone — not the browser's — because the chart and its date/time
  // pickers render the window in the profile zone, so a browser-local midnight would
  // display as some other wall-clock time. The graph panels own their own data; this is
  // the one window value they share, so both categories open on the same day.
  const baseChartFrom = useMemo(() => {
    const zone = getUserTimezone()
    const raised = toIsoString(summary?.alertRaisedTime)
    const parsed = raised ? DateTime.fromISO(raised).setZone(zone) : null

    return parsed?.isValid ? parsed.startOf('day') : DateTime.now().setZone(zone).startOf('day')
  }, [summary?.alertRaisedTime])

  const [showRecordActionModal, setShowRecordActionModal] = useState(false)

  const validationError = useMemo(() => {
    if (!alertId || !companyId || Number.isNaN(companyId)) {
      return 'Invalid URL.'
    }

    return ''
  }, [alertId, companyId])

  const fetchSummary = useCallback(async (): Promise<{ ok: boolean; ackPerformed: boolean }> => {
    if (!alertId || !companyId) {
      setSummary(null)
      setPageError('Invalid URL.')
      return { ok: false, ackPerformed: false }
    }

    setPageError('')
    setBlockingError(null)

    try {
      const response = await api.get<AlertSummaryRecord>({
        path:
          '/alerts/acknowledgement/information' +
          '?alertId=' +
          encodeURIComponent(alertId) +
          '&companyId=' +
          encodeURIComponent(companyId),
        origin: 'AlertAcknowledgementPage#fetchInformation',
      })

      if (response.error) {
        const blockingModalState = getBlockingModalState(response.code)

        if (blockingModalState) {
          setBlockingError(blockingModalState)
        } else {
          setPageError('Unable to load alert acknowledgement information. Please try again.')
        }
        setSummary(null)
        return { ok: false, ackPerformed: false }
      }

      let ackPerformed = false
      if (!response.data?.isUserAcknowledged) {
        const ackResponse = await api.post({
          path: '/alerts/acknowledgement/acknowledge' + (readOnly ? '?readOnly=true' : ''),
          origin: 'AlertAcknowledgementPage#acknowledgeAlert',
          body: { alertId, companyId },
        })

        if (ackResponse.error) {
          setPageError('Unable to acknowledge the alert. Please try again.')
          return { ok: false, ackPerformed: false }
        }
        ackPerformed = true
      }

      setSummary(response?.data ? response.data : null)
      return { ok: true, ackPerformed }
    } catch {
      setSummary(null)
      setPageError('An unknown error occurred.')
      return { ok: false, ackPerformed: false }
    }
  }, [alertId, companyId, api, readOnly])

  const fetchActions = useCallback(
    async ({ bypassCache = false }: { bypassCache?: boolean } = {}) => {
      if (!alertId || !companyId) return

      setIsActionsLoading(true)
      setActionsError('')

      // Append a unique param so the request URL misses the server's 30s cache and
      // reflects writes (acknowledge / record action) immediately.
      const cacheBust = bypassCache ? '&_=' + ++actionsCacheBustRef.current : ''

      try {
        const response = await api.get<ApiActionRow[]>({
          path:
            '/alerts/acknowledgement/actions' +
            '?alertId=' +
            encodeURIComponent(alertId) +
            '&companyId=' +
            encodeURIComponent(companyId) +
            cacheBust,
          origin: 'AlertAcknowledgementPage#fetchActions',
        })

        if (response.error) {
          throw new Error('Unable to load alert actions.')
        }

        const rows: AlertActionRecord[] = Array.isArray(response.data)
          ? response.data.map((row) => ({
              user: row.user,
              userId: row.userId,
              action: row.action,
              note: row.note,
              actionCreatedAt: new Date(row.actionCreatedAt),
            }))
          : []
        setActionRows(rows)
      } catch {
        setActionRows([])
        setActionsError('Unable to load alert actions.')
      } finally {
        setIsActionsLoading(false)
      }
    },
    [api, alertId, companyId]
  )

  useEffect(() => {
    if (validationError) {
      setSummary(null)
      setPageError(validationError)
      setIsPageLoading(false)
      return
    }

    let isActive = true

    const loadSummary = async () => {
      setIsPageLoading(true)
      const { ackPerformed } = await fetchSummary()

      if (isActive) {
        setIsPageLoading(false)
        // If we just recorded the acknowledgement, refresh the actions table with a
        // cache bypass so the new "Acknowledged" row shows immediately on open.
        if (ackPerformed) {
          void fetchActions({ bypassCache: true })
        }
      }
    }

    void loadSummary()

    return () => {
      isActive = false
    }
  }, [fetchSummary, fetchActions, validationError])

  useEffect(() => {
    if (!alertId || !companyId || validationError) return
    void fetchActions()
  }, [alertId, companyId, validationError, fetchActions])

  const alertMarkers = useMemo<AlertMarker | undefined>(() => {
    if (!summary?.alertRaisedTime) return undefined

    return {
      raisedTime: toIsoString(summary.alertRaisedTime),
      severityCode: Number(summary.severityCode),
      mpName: summary.mpName ?? null,
    }
  }, [summary])

  const summaryName = useMemo(() => {
    if (!summary) return 'Alert'
    const baseName = summary.alertName?.trim() || 'Alert'
    return summary.assetName ? `${summary.assetName} - ${baseName}` : baseName
  }, [summary])

  const handleSaveRecordAction = useCallback(
    async ({ alertActionStatusId, notes }: SaveRecordActionPayload) => {
      try {
        const response = await api.post<unknown, { alertId: string; alertActionStatusId: number; notes: string }>({
          path: '/alerts/acknowledgement/action?companyId=' + encodeURIComponent(companyId) + readOnlyParam,
          origin: 'AlertAcknowledgementPage#handleSaveRecordAction',
          body: {
            alertId: alertId || '',
            alertActionStatusId,
            notes,
          },
        })

        if (response.error) {
          throw new Error('Unable to save alert acknowledgement action.')
        }

        fetchActions({ bypassCache: true })
        return ''
      } catch {
        return 'Unable to save alert acknowledgement action.'
      }
    },
    [api, companyId, alertId, fetchActions, readOnlyParam]
  )

  const menuActions = useMemo<MenuAction[]>(
    () =>
      isStatusView
        ? [
            {
              id: 'go-to-alert-acknowledgement',
              label: 'Back',
              icon: <FiArrowLeft />,
              onClick: () => setView('alert-acknowledgement'),
            },
          ]
        : [
            // Managing action statuses is staff-only; Record Action stays open to everyone.
            ...(isStaff
              ? [
                  {
                    id: 'go-to-status-management',
                    label: 'Manage Statuses',
                    icon: <FiSettings />,
                    onClick: () => setView('status-management'),
                  },
                ]
              : []),
          ],
    [isStatusView, isStaff]
  )

  const visibleActions = !pageError && !isPageLoading && !!summary
  const isInitialLoading = !pageError && !blockingError && isPageLoading

  return (
    <div className="flex flex-col pt-4 pl-4 pr-4 pb-2 space-y-2">
      <div
        className={`relative z-20 mb-2 rounded-box bg-primary py-1 pr-4 ${titleInset} text-primary-content shadow-md`}
      >
        {view === 'status-management' ? (
          <div className="flex flex-col items-start gap-2 min-h-8 lg:flex-row lg:items-center lg:gap-4">
            <PageMenu actions={menuActions} />
            <PageTitle>{title}</PageTitle>
          </div>
        ) : (
          <div className="flex flex-col items-start justify-between gap-2 min-h-8 lg:flex-row lg:items-center">
            <PageTitle>{title}</PageTitle>
            {visibleActions ? <PageMenu actions={menuActions} /> : null}
          </div>
        )}
      </div>

      {blockingError ? (
        <AlertAcknowledgementErrorModal
          show={!!blockingError}
          title={blockingError.title}
          error={blockingError.error}
          onClose={() => navigate('/dashboards/default')}
        />
      ) : null}

      {pageError ? <Alert type="error" message={pageError} /> : null}

      {isInitialLoading ? <InitialAlertAcknowledgementSkeleton /> : null}

      {!pageError && !isPageLoading && summary ? (
        <AlertStatusesProvider alertConfigId={summary.alertConfigId || ''} companyId={companyId}>
          {view === 'alert-acknowledgement' ? (
            <>
              <AlertSummaryCard
                name={summaryName}
                severityCode={summary.severityCode || ''}
                alertRaisedTime={formatUserDate(summary.alertRaisedTime)}
                alertClearedTime={formatUserDate(summary.alertClearedTime)}
                raisedLocation={summary.raisedLocation}
                clearedLocation={summary.clearedLocation}
                onRecordActionClick={() => setShowRecordActionModal(true)}
                onHideMapClick={() => setShowMap(!showMap)}
                showMap={showMap}
              />
              <RecordActionModal
                show={showRecordActionModal}
                onCancel={() => setShowRecordActionModal(false)}
                onSaveRecordAction={handleSaveRecordAction}
              />
              <div className="grid grid-cols-1 lg:grid-cols-5 lg:grid-rows-[minmax(0,1fr)_minmax(0,1fr)] gap-4 h-[960px]">
                {summary.graphType === 'fuel' ? (
                  <FuelGraphPanel
                    assetId={summary.assetId}
                    companyId={companyId}
                    chartFrom={baseChartFrom}
                    alertMarkers={alertMarkers}
                    className={showMap ? 'lg:col-span-3' : 'lg:col-span-6'}
                  />
                ) : (
                  <TemperatureGraphPanel
                    assetId={summary.assetId}
                    companyId={companyId}
                    chartFrom={baseChartFrom}
                    alertMarkers={alertMarkers}
                    className={showMap ? 'lg:col-span-3' : 'lg:col-span-6'}
                  />
                )}
                <div
                  className={`lg:row-span-2 lg:col-span-2 lg:col-start-4 lg:row-start-1 rounded-md bg-white p-2 shadow-sm flex flex-col min-h-0 ${showMap ? '' : 'hidden'}`}
                >
                  {summary.raisedLatitude != null && summary.raisedLongitude != null ? (
                    <div className="flex-1 min-h-0">
                      <LocationMap
                        location={{
                          lat: summary.raisedLatitude,
                          lng: summary.raisedLongitude,
                          address: summary.assetName ?? 'Asset',
                          companyId,
                        }}
                      />
                    </div>
                  ) : (
                    <Alert type="info" message="No location available for this alert." />
                  )}
                </div>
                <div className={`min-h-0 ${showMap ? 'lg:col-span-3' : 'lg:col-span-6'}`}>
                  <AlertActions rows={actionRows} error={actionsError} isLoading={isActionsLoading} />
                </div>
              </div>
            </>
          ) : (
            <>
              <AlertSummaryCard name={summaryName} />
              <StatusManagement />
            </>
          )}
        </AlertStatusesProvider>
      ) : null}
    </div>
  )
}

export default AlertAcknowledgementPage
